/**
 * Auditor's Report — SA 705 (Revised) opinion-type variants.
 *
 * The Opinion section heading, the "We have audited…" scope paragraph, the
 * Opinion paragraph, and the Basis-for-Opinion section all vary with the
 * opinion type. Unmodified follows SA 700 (Revised); the three modified
 * variants follow SA 705 (Revised).
 */
import { richText } from './companyTypes';

export const OPINION_TYPE_OPTIONS = [
  { value: 'unmodified', label: 'Unmodified (Clean)' },
  { value: 'qualified', label: 'Qualified Opinion' },
  { value: 'adverse', label: 'Adverse Opinion' },
  { value: 'disclaimer', label: 'Disclaimer of Opinion' },
];

// SA 705: what gives rise to a qualified opinion — drives the Opinion
// paragraph's "effects" vs "possible effects" wording.
export const QUALIFICATION_BASIS_OPTIONS = [
  { value: 'misstatement', label: 'Material misstatement ("effects")' },
  { value: 'scope_limitation', label: 'Scope limitation ("possible effects")' },
];

export const isModifiedOpinion = (type) => Boolean(type) && type !== 'unmodified';

export function opinionHeading(type) {
  if (type === 'qualified') return 'Qualified Opinion';
  if (type === 'adverse') return 'Adverse Opinion';
  if (type === 'disclaimer') return 'Disclaimer of Opinion';
  return 'Opinion';
}

export function basisHeading(type) {
  if (type === 'qualified') return 'Basis for Qualified Opinion';
  if (type === 'adverse') return 'Basis for Adverse Opinion';
  if (type === 'disclaimer') return 'Basis for Disclaimer of Opinion';
  return 'Basis for Opinion';
}

/**
 * First paragraph of the Opinion section.
 * ctx = { coName (HTML), fyEnd (HTML), statements (string) }
 */
export function auditScopeParagraph(type, ctx) {
  const lead = type === 'disclaimer' ? 'We were engaged to audit' : 'We have audited';
  return `<p class="doc-para">${lead} the accompanying financial statements of <strong>${ctx.coName}</strong> ("the Company"), which comprise the Balance Sheet as at ${ctx.fyEnd}, ${ctx.statements}, and notes to the financial statements, including a summary of significant accounting policies and other explanatory information (hereinafter referred to as "the financial statements").</p>`;
}

/**
 * The Opinion paragraph itself.
 * ctx = { framework (string), fyEnd (HTML), profitLine (string),
 *         qualificationBasis ('misstatement' | 'scope_limitation') }
 *
 * SA 705 (Revised) qualifier wording turns on the NATURE of the matter:
 * a known material misstatement reads "except for the effects", while an
 * inability to obtain sufficient appropriate audit evidence (scope
 * limitation) must read "except for the POSSIBLE effects".
 */
export function opinionParagraph(type, ctx) {
  if (type === 'disclaimer') {
    return '<p class="doc-para">We do not express an opinion on the accompanying financial statements of the Company. Because of the significance of the matter described in the <em>Basis for Disclaimer of Opinion</em> section of our report, we have not been able to obtain sufficient appropriate audit evidence to provide a basis for an audit opinion on these financial statements.</p>';
  }
  const lead = 'In our opinion and to the best of our information and according to the explanations given to us,';
  if (type === 'adverse') {
    return `<p class="doc-para">${lead} because of the significance of the matter described in the <em>Basis for Adverse Opinion</em> section of our report, the aforesaid financial statements do not give the information required by the Companies Act, 2013 ("the Act") in the manner so required and do not give a true and fair view in conformity with ${ctx.framework}, and the accounting principles generally accepted in India, of the state of affairs of the Company as at ${ctx.fyEnd}, and its ${ctx.profitLine} for the year ended on that date.</p>`;
  }
  const qualifier = type === 'qualified'
    ? ` except for the ${ctx.qualificationBasis === 'scope_limitation' ? 'possible effects' : 'effects'} of the matter described in the <em>Basis for Qualified Opinion</em> section of our report,`
    : '';
  return `<p class="doc-para">${lead}${qualifier} the aforesaid financial statements give the information required by the Companies Act, 2013 ("the Act") in the manner so required and give a true and fair view in conformity with ${ctx.framework}, and the accounting principles generally accepted in India, of the state of affairs of the Company as at ${ctx.fyEnd}, and its ${ctx.profitLine} for the year ended on that date.</p>`;
}

/**
 * Body of the Basis section. For modified opinions, the user/AI-supplied
 * `modifiedText` describing the matter is inserted first; the standard SA
 * conduct paragraph follows (reworded for a disclaimer).
 */
export function basisParagraphs(type, modifiedText) {
  const parts = [];
  if (isModifiedOpinion(type)) {
    const what = type === 'qualified'
      ? 'the matter giving rise to the qualified opinion'
      : type === 'adverse'
        ? 'the matter giving rise to the adverse opinion'
        : 'the matter giving rise to the disclaimer of opinion';
    const body = modifiedText && String(modifiedText).trim()
      ? richText(modifiedText)
      : `<span class="doc-ph">[Describe in detail ${what} — the nature of the matter, the financial-statement line items affected, and, where practicable, a quantification of its effects.]</span>`;
    parts.push(`<p class="doc-para">${body}</p>`);
  }
  if (type === 'disclaimer') {
    parts.push('<p class="doc-para">We conducted our audit of the financial statements in accordance with the Standards on Auditing (SAs) specified under Section 143(10) of the Act. Our responsibilities under those Standards are further described in the <em>Auditor\'s Responsibilities for the Audit of the Financial Statements</em> section of our report. We are independent of the Company in accordance with the Code of Ethics issued by the Institute of Chartered Accountants of India ("ICAI") together with the ethical requirements that are relevant to our audit of the financial statements under the provisions of the Act and the Rules made thereunder, and we have fulfilled our other ethical responsibilities in accordance with these requirements. However, because of the matter described in the <em>Basis for Disclaimer of Opinion</em> section of our report, we were not able to obtain sufficient appropriate audit evidence to provide a basis for an audit opinion on the financial statements.</p>');
  } else {
    parts.push('<p class="doc-para">We conducted our audit of the financial statements in accordance with the Standards on Auditing (SAs) specified under Section 143(10) of the Act. Our responsibilities under those Standards are further described in the <em>Auditor\'s Responsibilities for the Audit of the Financial Statements</em> section of our report. We are independent of the Company in accordance with the Code of Ethics issued by the Institute of Chartered Accountants of India ("ICAI") together with the ethical requirements that are relevant to our audit of the financial statements under the provisions of the Act and the Rules made thereunder, and we have fulfilled our other ethical responsibilities in accordance with these requirements and the ICAI\'s Code of Ethics. We believe that the audit evidence obtained by us is sufficient and appropriate to provide a basis for our audit opinion on the financial statements.</p>');
  }
  return parts.join('\n');
}
